// PR-11.0 — Client-side cascade hook: when a league-linked booking gets
// moved or cancelled by admin, mirror the change to league_matches +
// post a system chat message + notify both teams.
//
// Ref: audit-reports/league-system/01-current-state-and-roadmap.md §3.2
//
// Design choice: lib/ helper (not React hook). Pure async functions that
// can be called from any mutation onSuccess handler. Easier to test than
// a React hook (no renderer needed for the integration callsites).
//
// Strategy: Hook-first (this PR). PR-11.1 adds a Postgres trigger as
// defense-in-depth for paths that bypass these UI-side calls (e.g.
// MATCHi-pushed updates via matchi_slot_id sync, future edge functions,
// direct admin SQL). Per investigation Q4: hybrid, hook-first.
//
// What the cascade does:
//
//   cascadeBookingMove(bookingId, newStart, newCourtId, opts?)
//     → If bookings.id is referenced by a league_matches.booking_id row,
//       update league_matches:
//         - rescheduled_date = YYYY-MM-DD from newStart (local TZ)
//         - rescheduled_time = HH:MM:SS from newStart (local TZ)
//         - court_id = newCourtId
//         - status = 'rescheduled'
//     → Insert match_chat_messages (is_system=true)
//     → Insert notifications row per team_member of both teams
//
//   cascadeBookingCancel(bookingId, opts?)
//     → Per investigation Q6: cancel UNLINKS (booking_id = NULL +
//       status='scheduled') rather than auto-WO. Admin must reschedule.
//       This avoids accidentally walking-over the match when the
//       cancellation reason is "double-booked court" or similar.
//     → System chat message + notifications same as move.
//
// Returns `{ linked: false }` for non-league bookings (the typical case).
// Callers can ignore the return value; the cascade is a fire-and-forget
// side-effect from the caller's perspective.

import { supabase } from "@/integrations/supabase/client";
import { splitTimestampLocal } from "./dateLocalSplit";
// UPPDRAG-8 PR-E: dual-write league cascade system messages.
import { sendLeagueMatchMessage } from "./chatDualWrite";

// Re-export for callers that want both the cascade + the formatter from
// this module's public API (and for backward-compat with prior imports).
export { splitTimestampLocal };

export interface CascadeMoveArgs {
  bookingId: string;
  newStartTime: Date;
  newCourtId: string;
  /** Optional human-readable reason; appended to the system chat message. */
  reason?: string | null;
  /** auth.uid() of the admin who initiated; null = system action. */
  adminUserId?: string | null;
}

export interface CascadeCancelArgs {
  bookingId: string;
  reason?: string | null;
  adminUserId?: string | null;
}

export interface CascadeResult {
  linked: boolean;
  matchId?: string;
  notifiedUserIds?: string[];
}

interface MatchLookupRow {
  id: string;
  league_id: string;
  home_team_id: string;
  away_team_id: string;
}

async function findLinkedMatch(bookingId: string): Promise<MatchLookupRow | null> {
  const { data, error } = await supabase
    .from("league_matches")
    .select("id, league_id, home_team_id, away_team_id")
    .eq("booking_id", bookingId)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

interface NotifyArgs {
  teamIds: string[];
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
}

/**
 * Insert one notifications row per distinct user_id across the given teams'
 * team_members rosters. Resolves the org_id from the team's league for
 * RLS scoping (notifications.organization_id is nullable but should be set).
 */
async function notifyTeams(args: NotifyArgs): Promise<string[]> {
  if (args.teamIds.length === 0) return [];
  // PostgREST embed: team_members → teams → leagues for org resolution
  const { data: members } = await supabase
    .from("team_members")
    .select("user_id, teams!inner(leagues!inner(organization_id))")
    .in("team_id", args.teamIds);
  if (!members?.length) return [];

  const orgId =
    (members[0]?.teams as unknown as { leagues: { organization_id: string } | null } | null)
      ?.leagues?.organization_id ?? null;
  if (!orgId) return [];

  const userIds = [...new Set(members.map((m) => m.user_id))];
  const rows = userIds.map((uid) => ({
    user_id: uid,
    organization_id: orgId,
    type: args.type,
    title: args.title,
    body: args.body,
    data: args.data as unknown as Record<string, unknown>,
  }));
  // notifications.data is Json; cast at the boundary so TS accepts the
  // Record<string, unknown> shape that the cascade emits.
  await supabase.from("notifications").insert(rows as never);
  return userIds;
}

export async function cascadeBookingMove(args: CascadeMoveArgs): Promise<CascadeResult> {
  const match = await findLinkedMatch(args.bookingId);
  if (!match) return { linked: false };

  const { dateString, timeString } = splitTimestampLocal(args.newStartTime);

  const { error: matchErr } = await supabase
    .from("league_matches")
    .update({
      rescheduled_date: dateString,
      rescheduled_time: timeString,
      court_id: args.newCourtId,
      status: "rescheduled",
    })
    .eq("id", match.id);
  if (matchErr) {
    // Non-fatal for the parent mutation — the booking move already
    // succeeded. Log + return; admin can manually reschedule the match.
    console.error("cascadeBookingMove: league_matches update failed", matchErr);
    return { linked: true, matchId: match.id };
  }

  const baseMsg = `Matchen flyttades till ${dateString} ${timeString.slice(0, 5)}`;
  const message = args.reason ? `${baseMsg}: ${args.reason}` : baseMsg;
  // UPPDRAG-8 PR-E: dual-write + structured metadata.
  await sendLeagueMatchMessage(supabase, {
    matchId: match.id,
    senderUserId: args.adminUserId ?? null,
    body: message,
    isSystem: true,
    data: {
      type: "booking_cascade",
      kind: "moved",
      booking_id: args.bookingId,
      new_date: dateString,
      new_time: timeString.slice(0, 5),
      reason: args.reason ?? null,
    },
  });

  const notifiedUserIds = await notifyTeams({
    teamIds: [match.home_team_id, match.away_team_id],
    type: "league_match_rescheduled",
    title: "Matchen har flyttats",
    body: `Matchen är nu ${dateString} ${timeString.slice(0, 5)}`,
    data: { match_id: match.id, league_id: match.league_id },
  });

  return { linked: true, matchId: match.id, notifiedUserIds };
}

export async function cascadeBookingCancel(args: CascadeCancelArgs): Promise<CascadeResult> {
  const match = await findLinkedMatch(args.bookingId);
  if (!match) return { linked: false };

  // Per investigation §3.2 Q6: cancel UNLINKS (booking_id = NULL +
  // status='scheduled') rather than auto-WO. Admin reschedules via
  // league module. Avoids accidental WO when reason was "wrong court".
  const { error: matchErr } = await supabase
    .from("league_matches")
    .update({ booking_id: null, status: "scheduled" })
    .eq("id", match.id);
  if (matchErr) {
    console.error("cascadeBookingCancel: league_matches update failed", matchErr);
    return { linked: true, matchId: match.id };
  }

  const baseMsg = "Bokningen avbokad — matchen behöver schemaläggas på nytt";
  const message = args.reason ? `${baseMsg}: ${args.reason}` : baseMsg;
  // UPPDRAG-8 PR-E: dual-write + structured metadata.
  await sendLeagueMatchMessage(supabase, {
    matchId: match.id,
    senderUserId: args.adminUserId ?? null,
    body: message,
    isSystem: true,
    data: {
      type: "booking_cascade",
      kind: "cancelled",
      booking_id: args.bookingId,
      reason: args.reason ?? null,
    },
  });

  const notifiedUserIds = await notifyTeams({
    teamIds: [match.home_team_id, match.away_team_id],
    type: "league_match_unscheduled",
    title: "Matchens bokning avbokades",
    body: "Matchen behöver schemaläggas på nytt",
    data: { match_id: match.id, league_id: match.league_id },
  });

  return { linked: true, matchId: match.id, notifiedUserIds };
}
